import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react'

import {
  isUserLoggedIn,
  getUserAccessToken,
  storeUserAccessToken,
  removeUserAccessToken,
} from '../utils/auth'
import { useApolloClient } from '@apollo/client'

interface AuthContextProps {
  isLoggedIn: boolean
  pastLoggedIn: boolean
  storeToken: (accessToken: string) => boolean
  deleteToken: () => Promise<boolean>
  accessToken?: string
}

export const AuthContext = createContext<AuthContextProps>({
  isLoggedIn: false,
  pastLoggedIn: false,
  storeToken: () => false,
  deleteToken: () => Promise.resolve(false),
  accessToken: undefined,
})

type Props = {
  children?: React.ReactNode
}

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>()
  const [isLoggedIn, setIsLoggedIn] = useState(isUserLoggedIn())
  const client = useApolloClient()

  useEffect(() => {
    setIsLoggedIn(isUserLoggedIn())
    setAccessToken(getUserAccessToken() || undefined)
  }, [])

  const pastLoggedIn = useRef(isLoggedIn)
  useEffect(() => {
    // login/logout
    pastLoggedIn.current = isLoggedIn
  }, [isLoggedIn])

  const storeToken = useCallback((accessToken: string) => {
    const loggedIn = storeUserAccessToken(accessToken)
    setIsLoggedIn(loggedIn)
    setAccessToken(accessToken)
    return loggedIn
  }, [])

  const deleteToken = useCallback(async () => {
    const loggedOut = removeUserAccessToken()
    setIsLoggedIn(!loggedOut)
    setAccessToken(undefined)
    await client.clearStore()
    return loggedOut
  }, [client])

  const value = useMemo(() => ({
    isLoggedIn,
    pastLoggedIn: pastLoggedIn.current,
    storeToken,
    deleteToken,
    accessToken,
  }), [isLoggedIn, storeToken, deleteToken, accessToken])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextProps => useContext(AuthContext)
